// track-cell — playback-mode track card on <bw-track-cell mode="stage">.
// Mirrors desktop Playback view: name + M/S + OUT + fader (no input/record —
// web is playback-only). Wires the cell's events to player.ts. VU is fed by
// the rAF loop via the returned setVu().

import '@bandway/ui-components';
import type { ShareManifest } from '@bandway/share-types';
import type { Player } from '../player.js';

type Track = ShareManifest['tracks'][number];

export interface TrackCellHandle {
  trackId: string;
  unmount: () => void;
  setVu: (level: number) => void;
}

export function mountTrackCell(host: Element, track: Track, player: Player): TrackCellHandle {
  const cell = document.createElement('bw-track-cell');
  cell.mode = 'stage';
  cell.track = {
    id: track.id,
    name: track.name,
    color: track.color,
    muted: !!track.initialMuted,
    soloed: !!track.initialSoloed,
    volume: track.initialVolume ?? 1,
    pan: typeof track.initialPan === 'number' ? track.initialPan : 0,
    outputChannel: -1,
  };
  cell.soloed = !!track.initialSoloed;
  // Экран пересобирается на повороте телефона (mount.ts), а плеер живёт дальше: микс — у
  // движка, и ячейка берёт его оттуда, а не дефолты манифеста (иначе M/S и уровни слушателя
  // выглядели бы сброшенными при звуке, который играет иначе). Дорожки, ещё не собранной
  // в движке, там нет — остаётся манифест (тот же приём, что у phone-screen.ts).
  const live = typeof player.getTrackStates === 'function' ? player.getTrackStates().get(track.id) : undefined;
  if (live) {
    cell.track = { ...cell.track, volume: live.gain, muted: live.muted, soloed: live.soloed };
    cell.soloed = live.soloed;
  }
  // Output routing is a hardware concept — inert on web; show a static label.
  cell.outputChannels = [];
  // Mark the desktop-rendered metronome track so it can be styled distinctly
  // (icon / muted accent). It still behaves as a normal mutable track.
  if (track.isMetronomeTrack) cell.dataset.metronome = 'true';

  // bw-track-cell is a CONTROLLED component: _onMuteClick/_onSoloClick only
  // emit the toggle event, they don't mutate the cell's own state. The consumer
  // must write the new value back to `cell.track` so the M/S buttons re-render
  // with their pressed/tone styling (otherwise the toggle is invisible).
  const onMute = (e: Event) => {
    const muted = (e as CustomEvent<{ muted: boolean }>).detail.muted;
    player.setTrackState(track.id, { muted });
    cell.track = { ...cell.track, muted };
  };
  const onSolo = (e: Event) => {
    const soloed = (e as CustomEvent<{ soloed: boolean }>).detail.soloed;
    player.setTrackState(track.id, { soloed });
    cell.track = { ...cell.track, soloed };
    cell.soloed = soloed;
  };
  const onVol = (e: Event) => player.setTrackState(track.id, { gain: (e as CustomEvent<{ value: number }>).detail.value });
  const onPan = (e: Event) => player.setPan(track.id, (e as CustomEvent<{ value: number }>).detail.value);

  cell.addEventListener('bw-mute-toggle', onMute);
  cell.addEventListener('bw-solo-toggle', onSolo);
  cell.addEventListener('bw-volume-input', onVol);
  cell.addEventListener('bw-volume-change', onVol);
  cell.addEventListener('bw-pan-change', onPan);

  host.appendChild(cell);

  return {
    trackId: track.id,
    unmount: () => {
      cell.removeEventListener('bw-mute-toggle', onMute);
      cell.removeEventListener('bw-solo-toggle', onSolo);
      cell.removeEventListener('bw-volume-input', onVol);
      cell.removeEventListener('bw-volume-change', onVol);
      cell.removeEventListener('bw-pan-change', onPan);
      try { host.removeChild(cell); } catch { /* noop */ }
    },
    setVu: (level: number) => { cell.vu = level; },
  };
}
